import { IconWithText } from 'common/icon/icon-with-text.js';
import { IconNameV1, IconV1 } from 'common/icon/iconV1.js';
import { BBQLegend } from 'components/map/marker-view/collections/bbq/legend.bbq.js';
import { StatusLegend } from 'components/map/marker-view/collections/marine-marker/legend.js';
import { KeyBarSoilMoisture } from 'components/map/marker-view/main/weatherStation/station-utils-soil-moisture.js';
import { KeyBarSoilTemperature } from 'components/map/marker-view/main/weatherStation/station-utils-soil-temp.js';
import { Fragment } from 'react';
import { IMapGeneralKeyContent, IMapKey, IMapKeyContent } from 'types/location.js';
import { MarkerTypeWaterActivity } from 'types/markerTypes/location-markers-type.js';
import { messageLog } from 'utilities/helpers/logger/message-logger.js';
import { getRating } from 'utilities/status/rating.js';
import { LegendTypes } from './legend-types.js';

type ILegendKeyProps = IMapKey & {
  showLabel?: boolean;
};

export const LegendKeys = (legendKey: ILegendKeyProps) => {
  // const key: IMapKey = { type, label, content, text, icon };
  // { type, label, content, text, icon, showLabel, legendImage }
  // @TODO These types could match the icon name which would tidy up this area
  switch (legendKey.type) {
    case LegendTypes.BBQ:
      return <BBQLegend mapKey={legendKey} />;
    case MarkerTypeWaterActivity.Paddleboarding:
    case MarkerTypeWaterActivity.Kayak:
      return <StatusLegend mapKey={legendKey} />;
    /** Used on /maps-radar/weather-stations/nz */
    case LegendTypes.WeatherStations:
      return (
        <>
          <Label showLabel={legendKey.showLabel} label={legendKey.label || 'Weather Stations'} />
          <IconV1 icon="key-weatherstations" />
        </>
      );
    /** Used on /maps-radar/weather-stations/nz */
    case LegendTypes.SoilMoistureKey:
      return <KeyBarSoilMoisture />;
    /** Used on /maps-radar/weather-stations/nz */
    case LegendTypes.SoilTemperatureKey:
      return <KeyBarSoilTemperature />;
    case LegendTypes.WarningSeverity:
      return (
        <>
          <Label showLabel label="SEVERE WEATHER" />
          <IconWithText classes="IconWithText--vCenter u-mB-xs" icon="warning-general-tri-red">
            <p className="u-mT-0 u-mB-0">Red Warning</p>
          </IconWithText>

          <IconWithText classes="IconWithText--vCenter u-mB-xs" icon="warning-general-hex-orange">
            <p className="u-mT-0 u-mB-0">Orange Warning</p>
          </IconWithText>

          <IconWithText classes="IconWithText--vCenter u-mB-xs" icon="warning-general-cir-yellow">
            <p className="u-mT-0 u-mB-0">Watch</p>
          </IconWithText>
        </>
      );
    default:
      messageLog.info(`No legend type for: ${legendKey.type}, falling back to GenericLegendKey`);
      // Fallback to using generic create key function
      return <GenericLegendKey mapKey={legendKey as IMapGeneralKeyContent} />;
  }
};

// Show label is only present if there's one legend-key, if there's more than one legend key it turns into an accordion (in legends component) which handles it
const Label = ({ showLabel, label }: { label: string; showLabel?: boolean }) =>
  showLabel ? <h4 className="u-mT-0 u-mB-xs">{label}</h4> : null;

export function createLegendKey(keyContent: IMapKeyContent, idx: number) {
  const { icon: iconForKey, label, rating, value } = keyContent;

  let icon = iconForKey;
  let labelEl = <small className="fineprint u-textBold u-block">{label}</small>;

  if (rating !== null && rating !== undefined) {
    // If there's a rating it could be for boating and surf so we need to get the appropriate colour rating
    // which will change the icon we use and colour the text
    const ratingData = getRating(rating);

    const isNone = rating === 0;

    icon = `${keyContent.icon}-${ratingData.icon}` as IconNameV1;
    labelEl = (
      <small className="fineprint u-textBold u-block">
        {isNone ? (
          <span className="u-textUppercase">{label}</span>
        ) : (
          <>
            <span className="u-textUppercase">{label}:&nbsp;</span>
            <span className={ratingData.textColorClass}>{ratingData.label}</span>
          </>
        )}
      </small>
    );
  }

  return (
    <Fragment key={idx}>
      {icon ? (
        <IconWithText icon={icon} iconClass="svg-fill-textSlate" classes="IconWithText--small IconWithText--vCenter">
          {labelEl}
        </IconWithText>
      ) : (
        labelEl
      )}

      {value && <p className="fineprint u-mT-0">{value}</p>}
    </Fragment>
  );
}

const GenericLegendKey = ({ mapKey: { text, content } }: { mapKey: IMapGeneralKeyContent }) => (
  <div data-comp="generic-legend">
    {text && <p>{text}</p>}
    {content?.map((keyContent, index) => createLegendKey(keyContent, index))}
  </div>
);
